import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Shield, Search, RefreshCw, CheckCircle, XCircle, AlertTriangle, Mail } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

interface EmailAuthCheck {
  id: string
  domainName: string
  spfStatus: string | null
  dkimStatus: string | null
  dmarcStatus: string | null
  dmarcPolicy: string | null
  mtaStsStatus: string | null
  tlsRptStatus: string | null
  bimiStatus: string | null
  daneStatus: string | null
  grade: string
  score: number
  checkedAt: string
}

function gradeColor(grade: string) {
  switch (grade) {
    case 'A': return 'bg-green-500/10 text-green-500 border-green-500/20'
    case 'B': return 'bg-blue-500/10 text-blue-500 border-blue-500/20'
    case 'C': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
    case 'D': return 'bg-orange-500/10 text-orange-500 border-orange-500/20'
    case 'F': return 'bg-red-500/10 text-red-500 border-red-500/20'
    default: return 'bg-muted text-muted-foreground'
  }
}

function statusIcon(status: string | null) {
  if (!status) return <span className="text-muted-foreground">-</span>
  if (status === 'pass' || status === 'valid' || status === 'found') {
    return <CheckCircle className="h-4 w-4 text-green-500" />
  }
  if (status === 'fail' || status === 'missing' || status === 'invalid') {
    return <XCircle className="h-4 w-4 text-red-500" />
  }
  return <AlertTriangle className="h-4 w-4 text-yellow-500" />
}

function CheckDomainDialog() {
  const [open, setOpen] = useState(false)
  const [domain, setDomain] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const checkMutation = useMutation({
    mutationFn: (domainName: string) =>
      apiClient.post(`/email-security/check/${encodeURIComponent(domainName)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-security'] })
      toast({ title: 'Check complete', description: 'Email security check finished' })
      setOpen(false)
      setDomain('')
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to check domain', variant: 'destructive' })
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Shield className="mr-2 h-4 w-4" />
          Check Domain
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Check Email Security</DialogTitle>
          <DialogDescription>
            Run SPF, DKIM, DMARC, and additional checks on a domain.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Input
            placeholder="example.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => checkMutation.mutate(domain)}
            disabled={!domain || checkMutation.isPending}
          >
            {checkMutation.isPending ? (
              <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Checking...</>
            ) : (
              'Run Check'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AuthStatusRow({ check }: { check: EmailAuthCheck }) {
  return (
    <tr className="border-b border-border hover:bg-muted/50">
      <td className="py-3 px-4 font-medium">{check.domainName}</td>
      <td className="py-3 px-4 text-center">{statusIcon(check.spfStatus)}</td>
      <td className="py-3 px-4 text-center">{statusIcon(check.dkimStatus)}</td>
      <td className="py-3 px-4 text-center">{statusIcon(check.dmarcStatus)}</td>
      <td className="py-3 px-4 text-center">
        {check.dmarcPolicy ? (
          <Badge variant="outline" className="text-xs">
            {check.dmarcPolicy}
          </Badge>
        ) : '-'}
      </td>
      <td className="py-3 px-4 text-center">{statusIcon(check.mtaStsStatus)}</td>
      <td className="py-3 px-4 text-center">{statusIcon(check.bimiStatus)}</td>
      <td className="py-3 px-4 text-center">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold border ${gradeColor(check.grade)}`}>
          {check.grade}
        </span>
      </td>
      <td className="py-3 px-4 text-center text-sm text-muted-foreground">
        {check.score}/100
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground">
        {new Date(check.checkedAt).toLocaleDateString()}
      </td>
    </tr>
  )
}

export function EmailSecurityPage() {
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['email-security'],
    queryFn: () => apiClient.get<EmailAuthCheck[]>('/email-security/status'),
  })

  const checks = data?.data || []
  const filtered = checks.filter((c) =>
    c.domainName.toLowerCase().includes(search.toLowerCase())
  )

  const gradeDistribution = checks.reduce<Record<string, number>>((acc, c) => {
    acc[c.grade] = (acc[c.grade] || 0) + 1
    return acc
  }, {})

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Email Security</h1>
          <p className="text-muted-foreground">
            SPF, DKIM, DMARC, and email authentication monitoring
          </p>
        </div>
        <CheckDomainDialog />
      </div>

      {/* Grade Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        {['A', 'B', 'C', 'D', 'F'].map((grade) => (
          <Card key={grade}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-lg font-bold border ${gradeColor(grade)}`}>
                  {grade}
                </span>
                <span className="text-2xl font-bold">{gradeDistribution[grade] || 0}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {grade === 'A' ? 'Excellent' : grade === 'B' ? 'Good' : grade === 'C' ? 'Fair' : grade === 'D' ? 'Poor' : 'Critical'}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search domains..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Auth Status Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Authentication Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No email security checks yet</p>
              <p className="text-sm">Check a domain to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium text-sm">Domain</th>
                    <th className="text-center py-3 px-4 font-medium text-sm">SPF</th>
                    <th className="text-center py-3 px-4 font-medium text-sm">DKIM</th>
                    <th className="text-center py-3 px-4 font-medium text-sm">DMARC</th>
                    <th className="text-center py-3 px-4 font-medium text-sm">Policy</th>
                    <th className="text-center py-3 px-4 font-medium text-sm">MTA-STS</th>
                    <th className="text-center py-3 px-4 font-medium text-sm">BIMI</th>
                    <th className="text-center py-3 px-4 font-medium text-sm">Grade</th>
                    <th className="text-center py-3 px-4 font-medium text-sm">Score</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Checked</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((check) => (
                    <AuthStatusRow key={check.id} check={check} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
